import { toRelativeUrl } from '@okta/okta-auth-js';
import { useOktaAuth } from '@okta/okta-react';
import React, { Children, PropsWithChildren, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useNavigate } from 'react-router-dom';
import Footer from '../../../Components/Footer/Footer';
import Sidebar from '../../../Components/Sidebar/Sidebar';
import applicationSettings from '../../../Core/Settings/ApplicationSettings';
import { useApplicationDispatch, useApplicationSelector } from '../../../Redux/ReduxStore';
import { selectUserGuid, selectUserName, selectUserTokens, setUser } from '../../../Redux/Slices/UserSlice';
import useUserSessionPingApiHook from '../../Apis/Hooks/useUserSessionPingApiHook';
import ApplicationHeader from '../../Components/Headers/ApplicationHeader/ApplicationHeader';
import PageLoadingSpinner from '../../Components/LoadingSpinner/PageLoadingSpinner';
import SmallDisclaimerText from '../../Components/Texts/SmallDisclaimerText/SmallDisclaimerText';
import SessionPage from '../../Pages/Session/SessionPage';
import browserService from '../../Services/BrowserService';
import { EventActionsEnum, EventCategoriesEnum } from '../../Services/Logging/DataLayerDefinitions';
import loggingService from '../../Services/Logging/LoggingService';
import trackingService from '../../Services/Tracking/TrackingService';
import ErrorBoundary from '../ErrorHandling/ErrorBoundary';
import styles from './OktaSecureRouteContainer.module.scss';

const SidebarContainer: React.FC<PropsWithChildren> = (props: PropsWithChildren) => (
    <>
        <ApplicationHeader />
        <div className={styles.okta_page_container}>
            <div className={styles.okta_page_sidebar}>
                <Sidebar />
            </div>
            <div className={styles.okta_page_content}>
                {Children.only(props.children)}
                <div className={styles.okta_page_disclaimer}>
                    <SmallDisclaimerText />
                </div>
            </div>
        </div>
    </>
);

const OktaSecureRouteContainer = () => {
    // Authentication hooks.
    const { authState, oktaAuth } = useOktaAuth();
    const dispatch = useApplicationDispatch();
    const userTokens = useApplicationSelector(selectUserTokens);
    const userGuid = useApplicationSelector(selectUserGuid);
    const userEmail = useApplicationSelector(selectUserName);

    // Session hooks.
    const { ready: translationLoaded } = useTranslation(applicationSettings.locize.namespace);
    const [sessionLoaded, setSessionLoaded] = useState(false);
    useUserSessionPingApiHook(sessionLoaded);

    // Navigation hooks.
    const navigate = useNavigate();

    useEffect(() => {
        browserService.setReactNavigate(navigate);
    }, [navigate]);

    useEffect(() => {
        if (!userGuid) {
            return;
        }

        loggingService.setUserGuid(userGuid);
        trackingService.setUserGuid(userGuid);
        trackingService.setUserEmail(userEmail);
    }, [userGuid, userEmail]);

    useEffect(() => {
        if (!authState) {
            return () => {};
        }

        if (!authState.isAuthenticated) {
            oktaAuth.signInWithRedirect();
            return () => {};
        }

        const setUserInStore = (newAccessToken: string, newIdToken: string) => {
            if (newAccessToken !== userTokens?.accessToken || newIdToken !== userTokens?.identityToken) {
                dispatch(
                    setUser({
                        accessToken: newAccessToken,
                        identityToken: newIdToken,
                    }),
                );
                loggingService.trackEventWithAnalytics(EventActionsEnum.UserAuthenticated, EventCategoriesEnum.Success);
            }
        };

        setUserInStore(authState.accessToken?.accessToken!, authState.idToken?.idToken!);

        const handleTokenRenewed = async () => {
            try {
                const accessToken = await oktaAuth.tokenManager.get('accessToken');
                const idToken = await oktaAuth.tokenManager.get('idToken');

                if (accessToken && idToken && 'accessToken' in accessToken && 'idToken' in idToken) {
                    setUserInStore(accessToken.accessToken, idToken.idToken);
                }
            } catch (error) {
                loggingService.trackException(error as Error, 'Failed to update tokens after renewal.');
                oktaAuth.signInWithRedirect();
            }
        };

        const handleTokenExpired = async () => {
            try {
                await oktaAuth.tokenManager.renew('accessToken');
                await oktaAuth.tokenManager.renew('idToken');
            } catch (error) {
                loggingService.trackException(error as Error, 'Failed to refresh tokens on expiry.');
                oktaAuth.signInWithRedirect();
            }
        };

        oktaAuth.tokenManager.on('renewed', handleTokenRenewed);
        oktaAuth.tokenManager.on('expired', handleTokenExpired);

        return () => {
            oktaAuth.tokenManager.off('renewed', handleTokenRenewed);
            oktaAuth.tokenManager.off('expired', handleTokenExpired);
        };
    }, [authState, authState?.isAuthenticated, oktaAuth]);

    if (!authState) {
        return <PageLoadingSpinner />;
    }

    if (!authState.isAuthenticated) {
        loggingService.trackEventWithAnalytics(EventActionsEnum.Navigation, EventCategoriesEnum.UnauthenticatedUserPageVisit);
        const originalUri = toRelativeUrl(window.location.href, window.location.origin);
        oktaAuth.setOriginalUri(originalUri);
        oktaAuth.signInWithRedirect();
        return <PageLoadingSpinner />;
    }

    // There is a slight delay in getting the access token into redux for use.
    // Therefore we will wait for it first, then render the authenticated pages.
    if (!userTokens?.accessToken || !translationLoaded) {
        return <PageLoadingSpinner />;
    }

    if (!sessionLoaded) {
        return <SessionPage setSessionLoaded={(isSessionLoaded: boolean) => setSessionLoaded(isSessionLoaded)} />;
    }

    return (
        <>
            <SidebarContainer>
                <ErrorBoundary>
                    <Outlet />
                </ErrorBoundary>
            </SidebarContainer>
            <Footer />
        </>
    );
};

export default OktaSecureRouteContainer;
